import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowLeft, Music2, Instagram, Image, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPost, setSelectedPost] = useState(null);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data.posts || []);
    } catch (err) {
      console.error('Error fetching posts:', err);
      setPosts([]);
    }
    setLoading(false);
  };

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'journey', label: 'My Journey' },
    { id: 'lesson', label: 'Lessons Learned' },
    { id: 'announcement', label: 'Announcements' },
    { id: 'update', label: 'Updates' }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const filteredPosts = selectedCategory === 'all'
    ? posts
    : posts.filter(p => p.category === selectedCategory);

  const openPost = async (post) => {
    try {
      const response = await axios.get(`${API}/blog/posts/${post.id}`);
      setSelectedPost(response.data);
    } catch (err) {
      setSelectedPost(post);
    }
  };

  return (
    <div className="blog-page" data-testid="blog-page">
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            className="post-detail-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedPost(null)}
            data-testid="post-detail-overlay"
          >
            <motion.article
              className="post-detail"
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 40 }}
              onClick={e => e.stopPropagation()}
              data-testid="post-detail"
            >
              <button className="post-detail-close" onClick={() => setSelectedPost(null)} data-testid="close-post-detail">
                <X size={24} />
              </button>
              <div className="post-detail-header">
                <span className={`post-category ${selectedPost.category}`}>{selectedPost.category}</span>
                <span className="post-date">
                  <Calendar size={14} /> {formatDate(selectedPost.created_at)}
                </span>
              </div>
              <h1>{selectedPost.title}</h1>
              {selectedPost.image_url && (
                <div className="post-detail-image">
                  <img src={selectedPost.image_url} alt={selectedPost.title} />
                </div>
              )}
              <div className="post-detail-content">
                {(selectedPost.content || '').split('\n').map((line, i) => {
                  const trimmed = line.trim();
                  if (!trimmed) return <br key={i} />;
                  if (trimmed.startsWith('- ')) return <li key={i}>{trimmed.substring(2)}</li>;
                  return <p key={i}>{trimmed}</p>;
                })}
              </div>
              {selectedPost.video_url && (
                <div className="post-detail-video">
                  <a href={selectedPost.video_url} target="_blank" rel="noopener noreferrer" className="video-link">
                    Watch Video
                  </a>
                </div>
              )}
              <div className="post-detail-footer">
                <span>By Adiajah @BlackGirlsTrade_</span>
              </div>
            </motion.article>
          </motion.div>
        )}
      </AnimatePresence>

      <section className="blog-hero">
        <motion.div className="blog-hero-content" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <span className="blog-badge">@BLACKGIRLSTRADE_</span>
          <h1>My Journey</h1>
          <p>Follow along as I learn to trade, one step at a time. Real lessons, real progress, real talk.</p>
          <div className="blog-social-links">
            <a href="https://www.tiktok.com/@BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-link tiktok">
              <Music2 size={18} /> TikTok
            </a>
            <a href="https://www.instagram.com/BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-link instagram">
              <Instagram size={18} /> Instagram
            </a>
          </div>
        </motion.div>
      </section>

      <section className="blog-content">
        <div className="category-tabs" data-testid="category-tabs">
          {categories.map(cat => (
            <button
              key={cat.id}
              className={`cat-tab ${selectedCategory === cat.id ? 'active' : ''}`}
              onClick={() => setSelectedCategory(cat.id)}
              data-testid={`cat-tab-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="blog-loading">
            <div className="spinner" />
            <p>Loading posts...</p>
          </div>
        ) : filteredPosts.length > 0 ? (
          <div className="posts-grid">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id || index}
                className="post-card"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={() => openPost(post)}
                data-testid={`post-card-${post.id}`}
              >
                {post.image_url && (
                  <div className="post-image">
                    <img src={post.image_url} alt={post.title} />
                  </div>
                )}
                <div className="post-card-body">
                  <div className="post-meta">
                    <span className={`post-category ${post.category}`}>{post.category}</span>
                    <span className="post-date">
                      <Calendar size={14} /> {formatDate(post.created_at)}
                    </span>
                  </div>
                  <h3>{post.title}</h3>
                  <p className="post-excerpt">{post.excerpt}</p>
                  <span className="read-more-link" data-testid={`read-post-${post.id}`}>Read More</span>
                </div>
              </motion.article>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <Image size={48} />
            <h3>No Posts Yet</h3>
            <p>Check back soon for updates on my trading journey!</p>
            <div className="follow-prompt">
              <p>In the meantime, follow me on social media:</p>
              <div className="social-buttons">
                <a href="https://www.tiktok.com/@BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-btn tiktok">
                  <Music2 size={18} /> @BlackGirlsTrade_
                </a>
                <a href="https://www.instagram.com/BlackGirlsTrade_" target="_blank" rel="noopener noreferrer" className="social-btn instagram">
                  <Instagram size={18} /> @BlackGirlsTrade_
                </a>
              </div>
            </div>
          </div>
        )}
      </section>

      <style>{`
        .blog-page { min-height: 100vh; padding-bottom: 60px; }
        .blog-hero { text-align: center; padding: 60px 24px 40px; }
        .blog-hero-content { max-width: 600px; margin: 0 auto; }
        .blog-badge {
          display: inline-block;
          padding: 6px 16px;
          background: rgba(0, 255, 148, 0.1);
          border: 1px solid rgba(0, 255, 148, 0.2);
          border-radius: 20px;
          color: var(--primary);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 1px;
          margin-bottom: 16px;
        }
        .blog-hero-content h1 { font-size: 2.5rem; margin: 0 0 12px; }
        .blog-hero-content p { color: var(--text-muted); font-size: 1.1rem; margin: 0 0 20px; }
        .blog-social-links { display: flex; gap: 12px; justify-content: center; }
        .social-link {
          display: flex; align-items: center; gap: 6px; padding: 8px 16px;
          border-radius: 8px; text-decoration: none; font-size: 14px; font-weight: 600; transition: all 0.2s;
        }
        .social-link.tiktok { background: rgba(255, 0, 80, 0.1); color: #ff0050; }
        .social-link.tiktok:hover { background: rgba(255, 0, 80, 0.2); }
        .social-link.instagram { background: rgba(225, 48, 108, 0.1); color: #e1306c; }
        .social-link.instagram:hover { background: rgba(225, 48, 108, 0.2); }
        .blog-content { max-width: 800px; margin: 0 auto; padding: 0 24px; }
        .category-tabs { display: flex; gap: 8px; margin-bottom: 32px; flex-wrap: wrap; }
        .cat-tab {
          padding: 8px 16px; background: var(--bg-paper); border: 1px solid var(--border);
          border-radius: 20px; color: var(--text-muted); cursor: pointer; font-size: 13px;
          font-weight: 600; transition: all 0.2s;
        }
        .cat-tab:hover { border-color: var(--primary); color: var(--text-primary); }
        .cat-tab.active { background: var(--primary); color: #0a0a0f; border-color: var(--primary); }
        .posts-grid { display: flex; flex-direction: column; gap: 20px; }
        .post-card {
          background: var(--bg-paper); border: 1px solid var(--border); border-radius: 16px;
          overflow: hidden; cursor: pointer; transition: all 0.3s;
        }
        .post-card:hover { border-color: var(--primary); transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,255,148,0.08); }
        .post-image img { width: 100%; height: 200px; object-fit: cover; }
        .post-card-body { padding: 24px; }
        .post-meta { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .post-category {
          padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 600;
          text-transform: capitalize; background: rgba(0,255,148,0.1); color: var(--primary);
        }
        .post-date { display: flex; align-items: center; gap: 4px; font-size: 13px; color: var(--text-muted); }
        .post-card-body h3 { margin: 0 0 8px; font-size: 1.2rem; }
        .post-excerpt { color: var(--text-muted); font-size: 14px; line-height: 1.6; margin: 0 0 16px; }
        .read-more-link {
          font-size: 14px; font-weight: 600; color: var(--primary); cursor: pointer;
          transition: opacity 0.2s;
        }
        .read-more-link:hover { opacity: 0.8; }
        .blog-loading { display: flex; flex-direction: column; align-items: center; gap: 16px; padding: 60px; color: var(--text-muted); }
        .spinner { width: 40px; height: 40px; border: 3px solid var(--border); border-top-color: var(--primary); border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .empty-state { text-align: center; padding: 60px 20px; color: var(--text-muted); }
        .empty-state svg { margin-bottom: 16px; }
        .empty-state h3 { color: var(--text-primary); margin: 0 0 8px; }
        .follow-prompt { margin-top: 24px; }
        .social-buttons { display: flex; gap: 12px; justify-content: center; margin-top: 12px; }
        .social-btn { display: flex; align-items: center; gap: 8px; padding: 10px 20px; border-radius: 8px; text-decoration: none; font-size: 14px; font-weight: 600; }
        .social-btn.tiktok { background: rgba(255,0,80,0.1); color: #ff0050; }
        .social-btn.instagram { background: rgba(225,48,108,0.1); color: #e1306c; }

        /* Post Detail Modal */
        .post-detail-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.85);
          display: flex; align-items: center; justify-content: center;
          z-index: 2000; padding: 20px; overflow-y: auto;
        }
        .post-detail {
          background: var(--bg-paper); border: 1px solid var(--border);
          border-radius: 20px; max-width: 700px; width: 100%;
          max-height: 90vh; overflow-y: auto; position: relative;
          padding: 40px;
        }
        .post-detail-close {
          position: absolute; top: 16px; right: 16px;
          background: var(--bg-subtle); border: 1px solid var(--border);
          border-radius: 50%; width: 40px; height: 40px;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; color: var(--text-muted); transition: all 0.2s;
        }
        .post-detail-close:hover { color: var(--text-primary); border-color: var(--text-primary); }
        .post-detail-header {
          display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
        }
        .post-detail h1 { font-size: 1.8rem; margin: 0 0 24px; line-height: 1.3; }
        .post-detail-image { margin-bottom: 24px; border-radius: 12px; overflow: hidden; }
        .post-detail-image img { width: 100%; display: block; }
        .post-detail-content { color: var(--text-secondary); font-size: 16px; line-height: 1.8; }
        .post-detail-content p { margin: 0 0 12px; }
        .post-detail-content li { margin: 0 0 6px; padding-left: 8px; list-style: disc inside; }
        .post-detail-content br { display: block; margin: 8px 0; content: ""; }
        .post-detail-footer {
          margin-top: 32px; padding-top: 20px; border-top: 1px solid var(--border);
          color: var(--text-muted); font-size: 14px;
        }
        .post-detail-video { margin-top: 20px; }
        .video-link {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 20px; background: var(--primary); color: #0a0a0f;
          border-radius: 8px; text-decoration: none; font-weight: 600;
        }
        @media (max-width: 768px) {
          .blog-hero-content h1 { font-size: 1.8rem; }
          .post-detail { padding: 24px; margin: 10px; }
          .post-detail h1 { font-size: 1.4rem; }
          .category-tabs { overflow-x: auto; flex-wrap: nowrap; }
        }
      `}</style>
    </div>
  );
};

export default Blog;
